import { useCallback, useEffect, useState } from 'react';
import type { BloomRuntimeConfig, BloomUser, LoginResponse, MeResponse } from '@apm/contracts';
import { config } from '../config.js';
import { promotesProgressOnLogin, resolveClientType } from '../client-type.js';
import { googleLoginBody } from './login-request.js';
import {
  clearToken,
  markJustLoggedIn,
  readToken,
  readViewerDeviceId,
  saveReturnTo,
  takeReturnTo,
  writeToken,
} from './session.js';

/**
 * ── 로그인 상태 ───────────────────────────────────────────────
 *
 * `checking` 을 따로 두는 이유는 콘솔이 같은 자리에서 배운 것이다 (`docs/06` A) —
 * 없으면 새로고침할 때마다 **로그인 화면이 한 번 번쩍인다.** 토큰은 살아 있는데 아직
 * 확인하지 못했을 뿐인데, 그 순간을 「로그인 안 됨」으로 그리면 그렇게 보인다.
 */
export type AuthStatus = 'checking' | 'authenticated' | 'anonymous';

export interface AuthApi {
  status: AuthStatus;
  user: BloomUser | null;
  /** `Authorization: Bearer` 로 나갈 값. 없으면 `null` */
  token: string | null;
  /**
   * 구글이 준 `id_token` 으로 로그인한다. 성공하면 되돌아갈 곳으로 **페이지를 옮긴다.**
   *
   * **토큰의 출처를 가리지 않는다** — PC·Android 는 GIS 팝업, 셸은 브리지 `signIn`
   * (v17 R3), 홈 화면 PWA 는 GIS redirect(v17 R9)가 같은 값을 여기로 가져온다.
   */
  loginWithGoogle: (idToken: string) => Promise<void>;
  logout: () => Promise<void>;
  /** 마지막 로그인 시도의 실패 사유. 화면이 그대로 보여 준다 */
  error: string | null;
}

/**
 * BLOOM 사용자 인증 (`docs/13` §3.1).
 *
 * ── 부팅 순서에서 이 훅의 자리 ────────────────────────────────
 *
 * ```
 * 1. GET /v1/bloom/config                    ← App
 * 2. requireLogin && 토큰 없음/만료 → returnTo 보관 → 로그인 화면
 * 3. 토큰이 있으면 GET /v1/auth/me 로 확인   ← 여기
 * 4. **그 다음에** TV2M SDK 초기화            ← useTv2m
 * ```
 *
 * **3 과 4 의 순서가 바뀌면 QR 복원이 죽는다.** `useTv2m` 의 기동은 모듈 스코프 약속으로
 * 단 한 번만 도는데, 그 안의 `checkDeferredLink()` 가 **살아 있는 링크를 첫 호출에서
 * 소비한다**(`consumedAt`, T5). 로그인 리다이렉트로 페이지가 리로드되면 모듈은 새로
 * 평가되지만 소비된 링크는 되살아나지 않는다 (`docs/12` §8 ④).
 *
 * 그래서 순서를 주석이 아니라 **컴포넌트 경계로** 강제한다 — `useTv2m` 을 부르는
 * 컴포넌트는 이 훅이 `authenticated`(또는 익명 허용)를 말한 뒤에야 마운트된다.
 */
export function useAuth(runtime: BloomRuntimeConfig): AuthApi {
  const [status, setStatus] = useState<AuthStatus>(() =>
    readToken() === null ? 'anonymous' : 'checking',
  );
  const [user, setUser] = useState<BloomUser | null>(null);
  const [token, setToken] = useState<string | null>(() => readToken());
  const [error, setError] = useState<string | null>(null);

  // 토큰이 있으면 살아 있는지 확인한다. **만료를 앱이 스스로 판정하지 않는다** —
  // 토큰은 서버가 서명한 것이고 TTL 도 서버 설정이다(720시간). 앱이 흉내 내면 두 판정이
  // 어긋나고, 그 증상은 「로그인돼 있는데 401 만 받는다」다.
  useEffect(() => {
    const stored = readToken();
    if (stored === null) return;

    let alive = true;
    void fetchMe(stored)
      .then((me) => {
        if (!alive) return;
        setUser(me.user);
        setStatus('authenticated');
      })
      .catch(() => {
        if (!alive) return;
        // 만료됐거나 서명 키가 바뀌었다. **토큰을 버린다** — 들고 있어 봐야 매 요청
        // 401 이고, 익명으로라도 시청은 돼야 한다.
        clearToken();
        setToken(null);
        setStatus('anonymous');
      });

    return () => {
      alive = false;
    };
  }, []);

  const loginWithGoogle = useCallback(async (idToken: string): Promise<void> => {
    setError(null);
    let result: LoginResponse;
    try {
      /*
       * **`deviceId` 를 함께 보낸다** (v16 `docs/38` §4.4 · v17 R1).
       *
       * ★ **v18 부터 서버가 이 값을 받아서 버린다** — 비로그인에 이력이 없어 **승격할
       * 것이 없어졌다**(`BloomAuthService.loginWithGoogle` 의 `_deviceId`). 계약에서
       * 빼지 않은 것은 배포된 앱이 보내고 있기 때문이고, 여기서 계속 보내는 것도 같은
       * 이유다 — **관측 가능한 차이가 없다.**
       *
       * 아래는 v16~v17 시점의 사정이고 **승격을 되살리는 날 그대로 유효하다.**
       *
       * 서버는 이 값으로 **기기 축 시청 진행을 계정으로 승격**했다. 계약에도 DTO 에도
       * `promoteProgress` 에도 자리가 있었는데 **여기서만 빠져 있었고**, 하네스와 통합
       * 테스트는 자기 손으로 실어 보내므로 초록이었다 — **실제 구글 로그인에서는 승격이
       * 한 번도 돈 적이 없다.**
       *
       * 호출부에서 받지 않고 여기서 읽는 이유는 **입구가 셋**(게이트·설정 시트·v17 의
       * 네이티브 버튼)이고 셋 다 같은 기기이기 때문이다. 넘기게 하면 새 입구가 생길
       * 때마다 빠뜨릴 수 있고, **빠뜨린 증상은 그때와 똑같이 「이어보기가 처음으로
       * 돌아간다」 하나다.**
       */
      /*
       * **TV 에서는 `deviceId` 를 싣지 않는다** (v17 R13 · `client-type.ts` 의
       * `promotesProgressOnLogin`). 서버 계약이 이미 「없으면 승격하지 않는다」이므로
       * **서버는 0줄이다.**
       *
       * 공유 기기에서 익명으로 본 사람은 로그인하는 그 사람이 아닐 수 있고, 승격은
       * `Math.max` 라 **되감기지 않고 앞으로만 뛴다** — 증상은 「내가 안 본 데까지 봤다고
       * 표시된다」이고 되돌릴 방법이 없다.
       */
      const promotable = promotesProgressOnLogin(resolveClientType());
      result = await postJson<LoginResponse>(
        '/v1/auth/google',
        googleLoginBody(idToken, promotable ? readViewerDeviceId() : null),
      );
    } catch (e) {
      setError(describeLoginError(e));
      return;
    }

    writeToken(result.accessToken);
    // `account_linked` 는 여기서 낼 수 없다 — 곧 페이지를 다시 열고, SDK 는 그 뒤에
    // 선다. 표식만 남기고 기동이 끝난 자리에서 낸다 (`docs/13` §8, `App.tsx`).
    markJustLoggedIn();

    /*
     * **되돌아갈 곳으로 페이지를 옮긴다.** 상태만 바꾸고 그대로 있으면 되지 않느냐 —
     * 되지 않는다. 로그인 화면은 원래 URL(예: `/install?ref=…`) 위에 떠 있고, 그 URL 을
     * SDK 가 **기동 시점에 한 번** 읽는다(`sdk.route(window.location.href)`). 페이지를
     * 다시 열어야 기동이 그 URL 을 보고, 그래야 QR 로 온 사람의 설치 ref 가 살아난다.
     *
     * `replace` 다 — 로그인 화면을 히스토리에 남기면 뒤로 가기가 그리로 돌아온다.
     */
    window.location.replace(takeReturnTo() ?? '/');
  }, []);

  /**
   * 로그아웃. 토큰을 버리고 페이지를 다시 연다.
   *
   * 서버가 할 일은 없다 — 세션 테이블이 없어 토큰을 취소할 수단이 없고(`docs/13` §4.1),
   * 전면 무효화는 `BLOOM_SESSION_SECRET` 교체다. 그래도 `POST /v1/auth/logout` 을
   * 부르는 이유는 그 자리가 계약에 있고, 서버가 나중에 할 일이 생겨도 앱을 고치지 않기
   * 위해서다.
   */
  const logout = useCallback(async (): Promise<void> => {
    const stored = readToken();
    clearToken();
    if (stored !== null) {
      try {
        await postJson('/v1/auth/logout', {}, stored);
      } catch {
        /* 서버가 할 일이 없으므로 실패해도 로그아웃은 성립한다 */
      }
    }
    window.location.replace('/');
  }, []);

  /*
   * 로그인 화면이 뜨는 순간 지금 URL 을 통째로 보관한다.
   *
   * 사용자가 무엇을 누르기 한참 전이므로 늦지 않다. 반대로 **로그인 성공 시점에
   * 읽으면 늦는다** — 그때는 이미 이 화면이 원래 URL 위에 떠 있을 뿐이고, 저장해 두지
   * 않았다면 새로고침 한 번에 목적지가 사라진다.
   */
  useEffect(() => {
    if (runtime.requireLogin && status === 'anonymous') {
      saveReturnTo(window.location.href, window.location.origin);
    }
  }, [runtime.requireLogin, status]);

  return { status, user, token, loginWithGoogle, logout, error };
}

async function fetchMe(token: string): Promise<MeResponse> {
  const response = await fetch(`${base()}/v1/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error(`me ${response.status}`);
  return (await response.json()) as MeResponse;
}

async function postJson<T>(path: string, body: unknown, token?: string): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token !== undefined) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(`${base()}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new HttpError(response.status);
  }
  // 204 는 본문이 없다. 항상 파싱하면 로그아웃이 실패한 것처럼 보인다.
  if (response.status === 204) return null as T;
  return (await response.json()) as T;
}

function base(): string {
  return config.serverUrl.replace(/\/+$/, '');
}

class HttpError extends Error {
  constructor(readonly status: number) {
    super(`HTTP ${status}`);
  }
}

/**
 * 실패 사유를 시청자의 말로 옮긴다.
 *
 * **401 과 나머지를 가르는 것이 중요하다.** 401 은 토큰 검증 실패(만료·`audience`
 * 불일치·테스트 사용자 미등록)라 다시 눌러도 같고, 그 밖은 대개 일시적이다.
 */
function describeLoginError(error: unknown): string {
  if (error instanceof HttpError && error.status === 401) {
    return '구글 계정을 확인하지 못했습니다. 이 계정이 테스트 사용자로 등록돼 있는지 확인해 주세요.';
  }
  return '로그인에 실패했습니다. 잠시 후 다시 시도해 주세요.';
}
